import { useEffect, useState, useMemo } from 'react';
import { API_URL } from '../config';
import { 
    Calendar, ChevronLeft, ChevronRight, Plus, X, Clock, 
    User, Building, Loader2, Edit, Trash2, Users, Sun, Moon, Sunrise, Settings, Palette
} from 'lucide-react';

/**
 * AgendaPage - Smart shift scheduling calendar
 * Weekly view with drag-and-drop support and quick assignment
 */
export default function AgendaPage() {
    const [turnos, setTurnos] = useState([]);
    const [colaboradores, setColaboradores] = useState([]);
    const [tiposTurno, setTiposTurno] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentWeek, setCurrentWeek] = useState(getWeekStart(new Date()));
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedColaborador, setSelectedColaborador] = useState(null);
    const [resumen, setResumen] = useState(null);
    const [formData, setFormData] = useState({
        colaborador_id: '',
        tipo_turno_id: '',
        fecha: '',
        hora_inicio: '',
        hora_fin: '',
        sucursal_id: '',
        notas: ''
    });
    
    // Tipos de turno management
    const [tiposModalOpen, setTiposModalOpen] = useState(false);
    const [editingTipo, setEditingTipo] = useState(null);
    const [tipoFormData, setTipoFormData] = useState({
        nombre: '',
        hora_inicio: '08:00',
        hora_fin: '17:00',
        color: '#F5B700',
        descripcion: ''
    });

    const token = localStorage.getItem('token');
    const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };

    // Get Monday of the week
    function getWeekStart(date) {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(d.setDate(diff));
    }

    // Get array of week days
    const weekDays = useMemo(() => {
        const days = [];
        for (let i = 0; i < 7; i++) {
            const day = new Date(currentWeek);
            day.setDate(currentWeek.getDate() + i);
            days.push(day);
        }
        return days;
    }, [currentWeek]);

    // Format date for display
    const formatDate = (date) => {
        return date.toLocaleDateString('es-CL', { 
            weekday: 'short', 
            day: 'numeric' 
        });
    };

    const formatDateISO = (date) => {
        return date.toISOString().split('T')[0];
    };

    useEffect(() => {
        fetchData();
    }, [currentWeek]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const fechaInicio = formatDateISO(weekDays[0]);
            const fechaFin = formatDateISO(weekDays[6]);

            const [turnosRes, colabRes, tiposRes, sucRes, resumenRes] = await Promise.all([
                fetch(`${API_URL}/api/turnos?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`, { headers }),
                fetch(`${API_URL}/api/colaboradores`, { headers }),
                fetch(`${API_URL}/api/turnos/tipos`, { headers }),
                fetch(`${API_URL}/api/colaboradores/sucursales`, { headers }),
                fetch(`${API_URL}/api/turnos/resumen`, { headers })
            ]);

            if (turnosRes.ok) setTurnos(await turnosRes.json());
            if (colabRes.ok) setColaboradores((await colabRes.json()).filter(c => c.estado === 'activo'));
            if (tiposRes.ok) setTiposTurno(await tiposRes.json());
            if (sucRes.ok) setSucursales(await sucRes.json());
            if (resumenRes.ok) setResumen(await resumenRes.json());
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            const res = await fetch(`${API_URL}/api/turnos`, {
                method: 'POST',
                headers,
                body: JSON.stringify(formData)
            });

            if (!res.ok) throw new Error('Error al crear turno');

            setModalOpen(false);
            resetForm();
            fetchData();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Eliminar este turno?')) return;

        try {
            await fetch(`${API_URL}/api/turnos/${id}`, {
                method: 'DELETE',
                headers
            });
            fetchData();
        } catch (err) {
            alert('Error al eliminar turno');
        }
    };

    const openNewTurno = (date, colaboradorId = null) => {
        setFormData({
            colaborador_id: colaboradorId || '',
            tipo_turno_id: '',
            fecha: formatDateISO(date),
            hora_inicio: '',
            hora_fin: '',
            sucursal_id: '',
            notas: ''
        });
        setSelectedDay(date);
        setSelectedColaborador(colaboradorId);
        setModalOpen(true);
    };

    const resetForm = () => {
        setFormData({
            colaborador_id: '', tipo_turno_id: '', fecha: '',
            hora_inicio: '', hora_fin: '', sucursal_id: '', notas: ''
        });
        setSelectedDay(null);
        setSelectedColaborador(null);
    };

    const navigateWeek = (direction) => {
        const newWeek = new Date(currentWeek);
        newWeek.setDate(currentWeek.getDate() + (direction * 7));
        setCurrentWeek(newWeek);
    };

    // Tipos de turno handlers
    const openTiposModal = () => {
        setTiposModalOpen(true);
        setEditingTipo(null);
        resetTipoForm();
    };

    const resetTipoForm = () => {
        setTipoFormData({
            nombre: '',
            hora_inicio: '08:00',
            hora_fin: '17:00',
            color: '#F5B700',
            descripcion: ''
        });
        setEditingTipo(null);
    };

    const openEditTipo = (tipo) => {
        setTipoFormData({
            nombre: tipo.nombre || '',
            hora_inicio: tipo.hora_inicio?.slice(0, 5) || '08:00',
            hora_fin: tipo.hora_fin?.slice(0, 5) || '17:00',
            color: tipo.color || '#F5B700',
            descripcion: tipo.descripcion || ''
        });
        setEditingTipo(tipo.id);
    };

    const handleTipoSubmit = async (e) => {
        e.preventDefault();
        
        try {
            const url = editingTipo 
                ? `${API_URL}/api/turnos/tipos/${editingTipo}`
                : `${API_URL}/api/turnos/tipos`;
            const method = editingTipo ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers,
                body: JSON.stringify(tipoFormData)
            });

            if (!res.ok) throw new Error('Error al guardar tipo de turno');

            resetTipoForm();
            fetchData();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleDeleteTipo = async (id) => {
        if (!confirm('¿Eliminar este tipo de turno?')) return;

        try {
            // Soft delete by setting estado = false
            await fetch(`${API_URL}/api/turnos/tipos/${id}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify({ estado: false })
            });
            fetchData();
        } catch (err) {
            alert('Error al eliminar tipo de turno');
        }
    };

    const goToToday = () => {
        setCurrentWeek(getWeekStart(new Date()));
    };

    // Get turnos for a specific day and colaborador
    const getTurnosForCell = (date, colaboradorId) => {
        return turnos.filter(t => 
            t.fecha?.split('T')[0] === formatDateISO(date) && 
            t.colaborador_id === colaboradorId
        );
    };

    // Check if date is today
    const isToday = (date) => {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    const getTipoTurnoIcon = (nombre) => {
        if (nombre?.toLowerCase().includes('mañana')) return <Sunrise size={12} />;
        if (nombre?.toLowerCase().includes('tarde')) return <Sun size={12} />;
        if (nombre?.toLowerCase().includes('noche')) return <Moon size={12} />;
        return <Clock size={12} />;
    };

    if (loading && turnos.length === 0) {
        return (
            <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
                <Loader2 size={48} className="spinning" style={{ color: 'var(--primary-color)' }} />
            </div>
        );
    }

    return (
        <div className="page">
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <div>
                    <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                        <Calendar size={28} />
                        Agenda de Turnos
                    </h1>
                    <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                        Planificación semanal del personal
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button 
                        onClick={openTiposModal}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.625rem 1rem',
                            background: 'white',
                            border: '1px solid var(--border-color)',
                            borderRadius: 'var(--radius-md)',
                            cursor: 'pointer',
                            color: 'var(--text-main)',
                            fontSize: '0.875rem'
                        }}
                    >
                        <Settings size={18} /> Tipos de Turno
                    </button>
                    <button 
                        onClick={() => openNewTurno(new Date())}
                        className="btn-primary"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                        <Plus size={18} /> Nuevo Turno
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            {resumen && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div className="report-card" style={{ padding: '1rem', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--primary-color)' }}>
                            {resumen.turnos_hoy || 0}
                        </div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Turnos Hoy</div>
                    </div>
                    <div className="report-card" style={{ padding: '1rem', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--secondary-color)' }}>
                            {resumen.colaboradores_hoy || 0}
                        </div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Trabajando Hoy</div>
                    </div>
                    <div className="report-card" style={{ padding: '1rem', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--accent-color)' }}>
                            {resumen.total_colaboradores || 0}
                        </div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Total Personal</div>
                    </div>
                </div>
            )}

            {/* Calendar Navigation */}
            <div className="report-card" style={{ padding: '1rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <button
                            onClick={() => navigateWeek(-1)}
                            style={{
                                background: 'var(--bg-input)',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '0.5rem',
                                cursor: 'pointer'
                            }}
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <button
                            onClick={goToToday}
                            style={{
                                background: 'var(--primary-color)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '0.5rem 1rem',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: '500'
                            }}
                        >
                            Hoy
                        </button>
                        <button
                            onClick={() => navigateWeek(1)}
                            style={{
                                background: 'var(--bg-input)',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '0.5rem',
                                cursor: 'pointer'
                            }}
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>
                    
                    <h2 style={{ margin: 0, fontSize: '1.125rem' }}>
                        {weekDays[0].toLocaleDateString('es-CL', { month: 'long', year: 'numeric' })}
                    </h2>

                    {/* Legend */}
                    <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem' }}>
                        {tiposTurno.slice(0, 4).map(tipo => (
                            <span 
                                key={tipo.id} 
                                style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                            >
                                <span style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '3px',
                                    background: tipo.color
                                }} />
                                {tipo.nombre}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Calendar Grid */}
            <div className="report-card" style={{ padding: 0, overflow: 'hidden' }}>
                {/* Header Row - Days */}
                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '180px repeat(7, 1fr)',
                    borderBottom: '1px solid var(--border-color)',
                    background: 'var(--bg-input)'
                }}>
                    <div style={{ 
                        padding: '1rem', 
                        fontWeight: '600', 
                        color: 'var(--text-secondary)',
                        borderRight: '1px solid var(--border-color)'
                    }}>
                        Colaborador
                    </div>
                    {weekDays.map((day, i) => (
                        <div 
                            key={i}
                            style={{ 
                                padding: '0.75rem 0.5rem', 
                                textAlign: 'center',
                                fontWeight: '600',
                                borderRight: i < 6 ? '1px solid var(--border-color)' : 'none',
                                background: isToday(day) ? 'rgba(79, 70, 229, 0.1)' : 'transparent',
                                color: isToday(day) ? 'var(--primary-color)' : 'var(--text-secondary)'
                            }}
                        >
                            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>
                                {day.toLocaleDateString('es-CL', { weekday: 'short' })}
                            </div>
                            <div style={{ fontSize: '1.25rem', fontWeight: '700' }}>
                                {day.getDate()}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Body Rows - Colaboradores */}
                {colaboradores.length === 0 ? (
                    <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                        <Users size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                        <p>No hay colaboradores activos</p>
                        <p style={{ fontSize: '0.875rem' }}>Agrega colaboradores desde la sección de Colaboradores</p>
                    </div>
                ) : (
                    <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        {colaboradores.map(colab => (
                            <div 
                                key={colab.id}
                                style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: '180px repeat(7, 1fr)',
                                    borderBottom: '1px solid var(--border-color)'
                                }}
                            >
                                {/* Colaborador Name */}
                                <div style={{ 
                                    padding: '0.75rem',
                                    borderRight: '1px solid var(--border-color)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    background: 'white'
                                }}>
                                    <div style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontWeight: '600',
                                        fontSize: '0.75rem',
                                        flexShrink: 0
                                    }}>
                                        {colab.name?.charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{ overflow: 'hidden' }}>
                                        <div style={{ 
                                            fontWeight: '600', 
                                            fontSize: '0.875rem',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {colab.name}
                                        </div>
                                        <div style={{ 
                                            fontSize: '0.7rem', 
                                            color: 'var(--text-secondary)' 
                                        }}>
                                            {colab.rol_nombre || 'Sin rol'}
                                        </div>
                                    </div>
                                </div>

                                {/* Day Cells */}
                                {weekDays.map((day, i) => {
                                    const cellTurnos = getTurnosForCell(day, colab.id);
                                    return (
                                        <div 
                                            key={i}
                                            onClick={() => openNewTurno(day, colab.id)}
                                            style={{ 
                                                padding: '0.5rem',
                                                borderRight: i < 6 ? '1px solid var(--border-color)' : 'none',
                                                background: isToday(day) ? 'rgba(79, 70, 229, 0.03)' : 'white',
                                                cursor: 'pointer',
                                                minHeight: '60px',
                                                transition: 'background 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-input)'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = isToday(day) ? 'rgba(79, 70, 229, 0.03)' : 'white'}
                                        >
                                            {cellTurnos.map(turno => (
                                                <div 
                                                    key={turno.id}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        background: turno.tipo_turno_color || 'var(--primary-color)',
                                                        color: 'white',
                                                        padding: '0.35rem 0.5rem',
                                                        borderRadius: '6px',
                                                        fontSize: '0.7rem',
                                                        marginBottom: '0.25rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: '0.25rem'
                                                    }}
                                                >
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                                        {getTipoTurnoIcon(turno.tipo_turno_nombre)}
                                                        {turno.hora_inicio?.slice(0, 5)} - {turno.hora_fin?.slice(0, 5)}
                                                    </span>
                                                    <button
                                                        onClick={() => handleDelete(turno.id)}
                                                        style={{
                                                            background: 'rgba(255,255,255,0.2)',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            padding: '2px',
                                                            cursor: 'pointer',
                                                            color: 'white',
                                                            display: 'flex'
                                                        }}
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                            {cellTurnos.length === 0 && (
                                                <div style={{ 
                                                    height: '100%', 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    color: 'var(--border-color)'
                                                }}>
                                                    <Plus size={16} />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal */}
            {modalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '500px' }}>
                        <button 
                            className="modal-close" 
                            onClick={() => { setModalOpen(false); resetForm(); }}
                        >
                            <X size={20} />
                        </button>
                        
                        <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Calendar size={24} />
                            Asignar Turno
                        </h2>

                        {selectedDay && (
                            <div style={{ 
                                background: 'var(--bg-input)', 
                                padding: '0.75rem 1rem', 
                                borderRadius: 'var(--radius-md)',
                                marginBottom: '1rem',
                                fontSize: '0.875rem'
                            }}>
                                📅 {selectedDay.toLocaleDateString('es-CL', { 
                                    weekday: 'long', 
                                    day: 'numeric', 
                                    month: 'long' 
                                })}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div className="form-group" style={{ marginBottom: '1rem' }}>
                                <label>Colaborador *</label>
                                <select
                                    required
                                    value={formData.colaborador_id}
                                    onChange={e => setFormData({...formData, colaborador_id: e.target.value})}
                                    style={{
                                        width: '100%',
                                        padding: '0.625rem 0.875rem',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--border-color)',
                                        background: 'var(--bg-input)',
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    <option value="">Seleccionar colaborador</option>
                                    {colaboradores.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group" style={{ marginBottom: '1rem' }}>
                                <label>Tipo de Turno *</label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                                    {tiposTurno.map(tipo => (
                                        <label
                                            key={tipo.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.75rem',
                                                background: formData.tipo_turno_id == tipo.id 
                                                    ? tipo.color 
                                                    : 'white',
                                                color: formData.tipo_turno_id == tipo.id ? 'white' : 'var(--text-main)',
                                                border: `2px solid ${tipo.color}`,
                                                borderRadius: 'var(--radius-md)',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <input
                                                type="radio"
                                                name="tipo_turno"
                                                value={tipo.id}
                                                checked={formData.tipo_turno_id == tipo.id}
                                                onChange={e => setFormData({...formData, tipo_turno_id: e.target.value})}
                                                style={{ display: 'none' }}
                                            />
                                            {getTipoTurnoIcon(tipo.nombre)}
                                            <div>
                                                <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>{tipo.nombre}</div>
                                                <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                                                    {tipo.hora_inicio?.slice(0, 5)} - {tipo.hora_fin?.slice(0, 5)}
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                <div className="form-group">
                                    <label>Fecha *</label>
                                    <input
                                        required
                                        type="date"
                                        value={formData.fecha}
                                        onChange={e => setFormData({...formData, fecha: e.target.value})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Sucursal</label>
                                    <select
                                        value={formData.sucursal_id}
                                        onChange={e => setFormData({...formData, sucursal_id: e.target.value})}
                                        style={{
                                            width: '100%',
                                            padding: '0.625rem 0.875rem',
                                            borderRadius: 'var(--radius-md)',
                                            border: '1px solid var(--border-color)',
                                            background: 'var(--bg-input)',
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        <option value="">Seleccionar</option>
                                        {sucursales.map(s => (
                                            <option key={s.id} value={s.id}>{s.nombre}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-group" style={{ marginBottom: '1rem' }}>
                                <label>Notas</label>
                                <input
                                    value={formData.notas}
                                    onChange={e => setFormData({...formData, notas: e.target.value})}
                                    placeholder="Observaciones opcionales..."
                                />
                            </div>

                            <div className="form-actions">
                                <button 
                                    type="button" 
                                    onClick={() => { setModalOpen(false); resetForm(); }}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--border-color)',
                                        background: 'white',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-primary">
                                    Asignar Turno
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Tipos de Turno Modal */}
            {tiposModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '700px' }}>
                        <button 
                            className="modal-close" 
                            onClick={() => { setTiposModalOpen(false); resetTipoForm(); }}
                        >
                            <X size={20} />
                        </button>
                        
                        <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Settings size={24} />
                            Gestionar Tipos de Turno
                        </h2>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                            {/* Form */}
                            <div>
                                <h3 style={{ fontSize: '1rem', marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                                    {editingTipo ? 'Editar Tipo' : 'Nuevo Tipo de Turno'}
                                </h3>
                                
                                <form onSubmit={handleTipoSubmit}>
                                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                                        <label>Nombre *</label>
                                        <input
                                            required
                                            value={tipoFormData.nombre}
                                            onChange={e => setTipoFormData({...tipoFormData, nombre: e.target.value})}
                                            placeholder="Ej: Turno Mañana, Turno Noche..."
                                        />
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                                        <div className="form-group">
                                            <label>Hora Inicio *</label>
                                            <input
                                                required
                                                type="time"
                                                value={tipoFormData.hora_inicio}
                                                onChange={e => setTipoFormData({...tipoFormData, hora_inicio: e.target.value})}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Hora Fin *</label>
                                            <input
                                                required
                                                type="time"
                                                value={tipoFormData.hora_fin}
                                                onChange={e => setTipoFormData({...tipoFormData, hora_fin: e.target.value})}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <Palette size={16} /> Color
                                        </label>
                                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                            <input
                                                type="color"
                                                value={tipoFormData.color}
                                                onChange={e => setTipoFormData({...tipoFormData, color: e.target.value})}
                                                style={{ width: '50px', height: '40px', padding: '2px', cursor: 'pointer' }}
                                            />
                                            <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                                                {['#F5B700', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899'].map(c => (
                                                    <button
                                                        key={c}
                                                        type="button"
                                                        onClick={() => setTipoFormData({...tipoFormData, color: c})}
                                                        style={{
                                                            width: '28px',
                                                            height: '28px',
                                                            borderRadius: '6px',
                                                            background: c,
                                                            border: tipoFormData.color === c ? '2px solid #333' : '2px solid transparent',
                                                            cursor: 'pointer'
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                                        <label>Descripción</label>
                                        <input
                                            value={tipoFormData.descripcion}
                                            onChange={e => setTipoFormData({...tipoFormData, descripcion: e.target.value})}
                                            placeholder="Descripción opcional..."
                                        />
                                    </div>

                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        {editingTipo && (
                                            <button
                                                type="button"
                                                onClick={resetTipoForm}
                                                style={{
                                                    padding: '0.625rem 1rem',
                                                    background: 'white',
                                                    border: '1px solid var(--border-color)',
                                                    borderRadius: 'var(--radius-md)',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Cancelar
                                            </button>
                                        )}
                                        <button type="submit" className="btn-primary" style={{ flex: 1 }}>
                                            {editingTipo ? 'Actualizar' : 'Crear Tipo de Turno'}
                                        </button>
                                    </div>
                                </form>
                            </div>

                            {/* List */}
                            <div>
                                <h3 style={{ fontSize: '1rem', marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                                    Tipos Existentes ({tiposTurno.length})
                                </h3>
                                
                                <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                    {tiposTurno.length === 0 ? (
                                        <div style={{ 
                                            textAlign: 'center', 
                                            padding: '2rem', 
                                            background: 'var(--bg-input)', 
                                            borderRadius: 'var(--radius-md)',
                                            color: 'var(--text-secondary)'
                                        }}>
                                            <Clock size={32} style={{ opacity: 0.5, marginBottom: '0.5rem' }} />
                                            <p style={{ margin: 0 }}>No hay tipos de turno</p>
                                            <p style={{ margin: 0, fontSize: '0.75rem' }}>Crea el primero usando el formulario</p>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            {tiposTurno.map(tipo => (
                                                <div 
                                                    key={tipo.id}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        padding: '0.75rem',
                                                        background: 'white',
                                                        border: '1px solid var(--border-color)',
                                                        borderRadius: 'var(--radius-md)',
                                                        borderLeft: `4px solid ${tipo.color}`
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                        <div style={{
                                                            width: '36px',
                                                            height: '36px',
                                                            borderRadius: '8px',
                                                            background: tipo.color,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: 'white'
                                                        }}>
                                                            {getTipoTurnoIcon(tipo.nombre)}
                                                        </div>
                                                        <div>
                                                            <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>{tipo.nombre}</div>
                                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                                                {tipo.hora_inicio?.slice(0, 5)} - {tipo.hora_fin?.slice(0, 5)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                                                        <button
                                                            onClick={() => openEditTipo(tipo)}
                                                            style={{
                                                                background: 'rgba(79, 70, 229, 0.1)',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                padding: '0.4rem',
                                                                cursor: 'pointer',
                                                                color: 'var(--primary-color)'
                                                            }}
                                                        >
                                                            <Edit size={14} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteTipo(tipo.id)}
                                                            style={{
                                                                background: 'rgba(239, 68, 68, 0.1)',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                padding: '0.4rem',
                                                                cursor: 'pointer',
                                                                color: 'var(--danger-color)'
                                                            }}
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div style={{ marginTop: '1.5rem', textAlign: 'right' }}>
                            <button
                                onClick={() => { setTiposModalOpen(false); resetTipoForm(); }}
                                className="btn-primary"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
